import { FrameInputNode } from '@/components/canvas/nodes/FrameInputNode'
import { FrameOutputNode } from '@/components/canvas/nodes/FrameOutputNode'

import React, { useCallback } from 'react'
import { useDrop } from 'react-dnd'
import {
  type Node,
  type Edge,
  type OnNodesChange,
  type OnEdgesChange,
  type OnConnect,
  type ReactFlowInstance,
  type NodeTypes,
} from '@xyflow/react'
import GenieCanvas from '@/components/canvas/GenieCanvas'
import { TextFileNode } from '@/components/canvas/nodes/TextFileNode'
import { ImageFileNode } from '@/components/canvas/nodes/ImageFileNode'
import { TextNodeNode } from '@/components/canvas/nodes/TextNodeNode'
import { ImageNodeNode } from '@/components/canvas/nodes/ImageNodeNode'
import { CommentNodeNode } from '@/components/canvas/nodes/CommentNodeNode'
import { VideoNodeNode } from '@/components/canvas/nodes/VideoNodeNode'
import { VideoFileNode } from '@/components/canvas/nodes/VideoFileNode'
import { AudioFileNode } from '@/components/canvas/nodes/AudioFileNode'
import { MaskNodeNode } from '@/components/canvas/nodes/MaskNodeNode'
import { DataTableNode } from '@/components/canvas/nodes/DataTableNode'
import { VariationGeneratorNode } from '@/components/canvas/nodes/VariationGeneratorNode'
import { PromptComposerNode } from '@/components/canvas/nodes/PromptComposerNode'
import { BatchGeneratorNode } from '@/components/canvas/nodes/BatchGeneratorNode'
import { GalleryGroupNode } from '@/components/canvas/nodes/GalleryGroupNode'
import { GalleryContainerNode } from '@/components/canvas/nodes/GalleryContainerNode'
import { LoraModelNode } from '@/components/canvas/nodes/LoraModelNode'
import { ShaderNodeNode } from '@/components/canvas/nodes/ShaderNodeNode'
import { ThreeDNodeNode } from '@/components/canvas/nodes/ThreeDNodeNode'
import { GenieEdge } from '@/components/canvas/edges/GenieEdge'
import { createTextFileNode, createImageFileNode, createVideoFileNode, createAudioFileNode } from '@/lib/canvas'
import { createLoraModelNode } from '@/lib/canvas/utils/nodeFactory'
import type { GenieCanvasNode, HandleMeta } from '@/lib/canvas/types'
import { toReactFlowNode } from '@/lib/canvas/utils/toReactFlowNode'
import { ItemTypes } from '@/components/canvas/nodes/components/ui/content-card'
import type { CanvasToolDragItem } from '@/lib/canvas/item-types'
import { LORA_MODEL_DRAG_TYPE } from '@/components/canvas/files-panel/models-tab-content'
import { useCanvasFileDrop } from '@/hooks/canvas/useCanvasFileDrop'
import { libraryFileNodeKind } from '@/components/canvas/nodes/utils/library-file-drop'
import { toast } from '@/components/ui/toast'
import { createLibraryNode, LIBRARY_MODEL_DRAG_TYPE, LIBRARY_SHADER_DRAG_TYPE, type LibraryDropItem } from '@/components/canvas/library/library-drag'
import { useCanvasBridgeStore } from '@/stores/useCanvasBridgeStore'
import { FEATURES } from '@/lib/features'

/** Drop item from content card drag */
interface ContentCardDropItem {
  type: 'text' | 'image' | 'video'
  content: string
  metadata?: {
    generatorNodeId?: string
    generatedParams?: {
      model?: string
      parameters?: Record<string, unknown>
    }
    generationSnapshot?: Record<string, unknown>
    dimensions?: { width: number; height: number }
  }
}

/** Drop item from sidebar file drag */
interface SidebarFileDropItem {
  uuid: string
  filename: string
  contentType: string
  publicUrl: string | null
  metadata?: Record<string, unknown>
}

/** Drop item from Models tab LoRA drag */
interface LoraDropItem {
  id: string
  name: string
  triggerWord: string | null
  baseModel: string
  thumbnailUrl?: string | null
}

/* ---- Node & Edge type registries ---- */

export const builtinNodeTypes = {
  FrameInput: FrameInputNode,
  FrameOutput: FrameOutputNode,
  TextFile: TextFileNode,
  ImageFile: ImageFileNode,
  VideoFile: VideoFileNode,
  AudioFile: AudioFileNode,
  TextNode: TextNodeNode,
  ImageNode: ImageNodeNode,
  VideoNode: VideoNodeNode,
  CommentNode: CommentNodeNode,
  MaskNode: MaskNodeNode,
  DataTable: DataTableNode,
  VariationGenerator: VariationGeneratorNode,
  PromptComposer: PromptComposerNode,
  BatchGenerator: BatchGeneratorNode,
  GalleryGroup: GalleryGroupNode,
  GalleryContainer: GalleryContainerNode,
  LoraModel: LoraModelNode,
  ShaderNode: ShaderNodeNode,
}

/** 3D generation is unfinished, so the node stays out of the registry: it
 *  cannot be added, and a canvas saved with one falls back to a plain node
 *  instead of showing a Generate button that does nothing. */
const gatedNodeTypes: NodeTypes = FEATURES.NODE_3D ? { ThreeDNode: ThreeDNodeNode } : {}

/** Node types defined at module scope so the reference is stable across renders.
 *  React Flow re-mounts all nodes if nodeTypes changes reference. */
const nodeTypes: NodeTypes = {
  ...builtinNodeTypes,
  ...gatedNodeTypes,
}

const edgeTypes = {
  default: GenieEdge,
  bezier: GenieEdge,
}

function getNodeHandles(node: Node): HandleMeta[] {
  const data = node?.data as Record<string, unknown> | undefined
  const dataHandles = data?.handles as HandleMeta[] | undefined
  const nodeHandles = (node as Node & { handles?: HandleMeta[] })?.handles
  return dataHandles || nodeHandles || []
}

/* ---- Props ---- */

interface CanvasDropZoneProps {
  reactFlowInstance?: ReactFlowInstance | null
  canvasRef?: React.RefObject<ReactFlowInstance | null>
  onInit?: (instance: ReactFlowInstance) => void
  canvasId: string | null
  nodes: Node[]
  edges: Edge[]
  onNodesChange?: OnNodesChange | ((nodes: Node[]) => void)
  onEdgesChange?: OnEdgesChange | ((edges: Edge[]) => void)
  onConnect?: OnConnect
  onNodeClick?: (event: React.MouseEvent, node: Node) => void
  onEdgeClick?: (event: React.MouseEvent, edge: Edge) => void
  onPaneClick?: (event: React.MouseEvent) => void
  onPaneContextMenu?: (event: MouseEvent | React.MouseEvent) => void
  onNodeContextMenu?: (event: React.MouseEvent, node: Node) => void
  onEdgeContextMenu?: (event: React.MouseEvent, edge: Edge) => void
  onSelectionChange?: ((params: { nodes: Node[]; edges: Edge[] }) => void) | ((nodeIds: string[]) => void)
  onViewportChange?: (viewport: { x: number; y: number; zoom: number }) => void
  onNodeDragStop?: (event: React.MouseEvent, node: Node, nodes: Node[]) => void
  onConnectionDropped?: (params: {
    sourceNodeId: string
    sourceHandleId: string
    sourceHandleType: 'input' | 'output'
    sourceHandleDataType: string
    position: { x: number; y: number }
  }) => void
  viewport: { x: number; y: number; zoom: number }
  canvasSize: { width: number; height: number }
  theme?: 'light' | 'dark' | 'auto'
  isPanning?: boolean
  isSpacePressed?: boolean
  /** When set, overrides the cursor (e.g. 'crosshair' for placement mode) */
  placementCursor?: string
  onMouseDown?: (e: React.MouseEvent) => void
  onMouseUp?: () => void
  config?: Record<string, unknown>
  setNodes?: React.Dispatch<React.SetStateAction<Node[]>>
  setEdges?: React.Dispatch<React.SetStateAction<Edge[]>>
  /** Show zoom controls panel */
  showControls?: boolean
  /** Show minimap panel */
  showMiniMap?: boolean
  /** A tool dragged out of the catalogue; the canvas places its node where the drag ended. */
  onToolDrop?: (tool: string, screenPosition: { x: number; y: number }) => void
}

/* ---- Component ---- */

export const CanvasDropZone = React.memo(function CanvasDropZone({
  reactFlowInstance,
  canvasRef,
  onInit,
  canvasId,
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  onConnect,
  onNodeClick,
  onEdgeClick,
  onPaneClick,
  onPaneContextMenu,
  onNodeContextMenu,
  onEdgeContextMenu,
  viewport,
  canvasSize,
  theme,
  isPanning,
  isSpacePressed,
  placementCursor,
  onSelectionChange,
  onViewportChange,
  onNodeDragStop,
  onMouseDown,
  onMouseUp,
  config,
  setNodes,
  setEdges,
  showControls,
  showMiniMap,
  onToolDrop,
}: CanvasDropZoneProps) {
  // Store React Flow instance in canvasRef when initialized
  const handleInit = useCallback((instance: ReactFlowInstance) => {
    if (canvasRef) {
      (canvasRef as React.MutableRefObject<ReactFlowInstance | null>).current = instance
    }
    onInit?.(instance)
  }, [canvasRef, onInit])

  // Propagate viewport changes (zoom, pan) to parent
  const handleMoveEnd = useCallback((_event: MouseEvent | TouchEvent | null, vp: { x: number; y: number; zoom: number }) => {
    onViewportChange?.(vp)
  }, [onViewportChange])

  // Native file drop handlers (for files from file system)
  const fileDropHandlers = useCanvasFileDrop({
    canvasRef: (canvasRef || { current: reactFlowInstance }) as React.RefObject<ReactFlowInstance>,
    canvasId,
    viewport,
    canvasSize,
    containerSelector: '.canvas-page',
    setNodes: setNodes as React.Dispatch<React.SetStateAction<Node[]>>,
  })

  // React DnD drop handler (for internal drag/drop - content cards and sidebar files)
  const [{ isOver, canDrop }, drop] = useDrop<ContentCardDropItem | SidebarFileDropItem | LoraDropItem | LibraryDropItem | CanvasToolDragItem, { dropped: boolean; success?: boolean; nodeId?: string }, { isOver: boolean; canDrop: boolean }>({
    accept: [ItemTypes.CREATED_TEXT, ItemTypes.CREATED_IMAGE, ItemTypes.CREATED_VIDEO, ItemTypes.SIDEBAR_FILE, ItemTypes.CANVAS_TOOL, LORA_MODEL_DRAG_TYPE, LIBRARY_MODEL_DRAG_TYPE, LIBRARY_SHADER_DRAG_TYPE],
    canDrop: (_item, monitor) => {
      const clientOffset = monitor.getClientOffset()
      if (!clientOffset) return false
      // A tool always places its node where it was dropped, even over another node.
      if (monitor.getItemType() === ItemTypes.CANVAS_TOOL) return true

      // Don't allow drop over nodes
      const nodeElements = document.querySelectorAll('[data-node-id]')
      for (const nodeEl of nodeElements) {
        const rect = nodeEl.getBoundingClientRect()
        if (
          clientOffset.x >= rect.left &&
          clientOffset.x <= rect.right &&
          clientOffset.y >= rect.top &&
          clientOffset.y <= rect.bottom
        ) {
          return false
        }
      }
      return true
    },
    drop: (item: ContentCardDropItem | SidebarFileDropItem | LoraDropItem | LibraryDropItem | CanvasToolDragItem, monitor) => {
      const instance = canvasRef?.current || reactFlowInstance
      const clientOffset = monitor.getClientOffset()
      if (!clientOffset) return
      if (!instance) {
        console.warn('[CanvasDropZone] drop fired but ReactFlow instance not ready yet')
        return
      }

      // A tool from the catalogue: the host owns node creation, so it only needs
      // the tool and where the pointer let go.
      if ('tool' in item) {
        onToolDrop?.(item.tool, clientOffset)
        return { dropped: true, success: true }
      }

      // Use React Flow's built-in coordinate conversion
      const position = instance.screenToFlowPosition({
        x: clientOffset.x,
        y: clientOffset.y,
      })

      if ('catalogModel' in item || 'librarySketch' in item) {
        const node = createLibraryNode(item, position, canvasId)
        const rfNode = toReactFlowNode(node, position)
        setNodes?.(previous => [...previous, rfNode])
        return { dropped: true, success: true, nodeId: node.id }
      }

      // Handle LoRA drop (from Models tab) — spawn a LoraModel node; user wires edge manually.
      // Detection: item has `id` and `triggerWord` but NOT `uuid` or `content`.
      if ('triggerWord' in item && 'baseModel' in item) {
        const loraItem = item as LoraDropItem
        const newNode = createLoraModelNode(position, {
          id: loraItem.id,
          name: loraItem.name,
          triggerWord: loraItem.triggerWord,
          baseModel: loraItem.baseModel,
          thumbnailUrl: loraItem.thumbnailUrl ?? null,
        })
        if (newNode && canvasId) {
          if (newNode.component?.state) {
            newNode.component.state.canvasId = canvasId
          }
          const rfNode = toReactFlowNode(newNode, position)
          setNodes?.((prev) => prev.some((n) => n.id === rfNode.id) ? prev : [...prev, rfNode])
          return { dropped: true, success: true, nodeId: newNode.id }
        }
        return { dropped: false }
      }

      // Handle sidebar file drop
      if ('uuid' in item) {
        const sidebarItem = item as SidebarFileDropItem
        const versions = sidebarItem.metadata?.versions as Record<string, unknown> | undefined
        const original = versions?.original as Record<string, unknown> | undefined
        const dimensions = (original?.width && original?.height)
          ? { width: original.width as number, height: original.height as number }
          : undefined
        // Route by the file's type: anything else in an ImageFile node ends up
        // as a broken <img>, so files no node can show are refused instead.
        const kind = libraryFileNodeKind(sidebarItem.contentType, sidebarItem.filename)
        if (!kind) {
          const extension = sidebarItem.filename.includes('.') ? sidebarItem.filename.split('.').pop()!.toUpperCase() : 'This'
          toast.error(`${extension} files can't go on the canvas yet. Drop images, videos or audio.`)
          return { dropped: false }
        }
        const publicUrl = sidebarItem.publicUrl ?? undefined
        const newNode = kind === 'video'
          ? createVideoFileNode(position, publicUrl)
          : kind === 'audio'
            ? createAudioFileNode(position, publicUrl)
            : createImageFileNode(position, publicUrl, undefined, dimensions)
        if (newNode && canvasId) {
          if (newNode.component?.state) {
            newNode.component.state.canvasId = canvasId
            if (kind !== 'image') {
              // VideoFile and AudioFile read state.fileMetadata; link it to the
              // existing upload instead of the factory's placeholder name.
              newNode.component.state.fileUuid = sidebarItem.uuid
              newNode.component.state.fileMetadata = {
                ...(sidebarItem.metadata || {}),
                publicUrl: sidebarItem.publicUrl || '',
                filename: sidebarItem.filename,
                contentType: sidebarItem.contentType,
              }
            }
          }
          const rfNode = toReactFlowNode(newNode, position)
          rfNode.data = { ...rfNode.data, fileUuid: sidebarItem.uuid, fileMetadata: sidebarItem.metadata }
          setNodes?.((prev) => prev.some(n => n.id === rfNode.id) ? prev : [...prev, rfNode])
          return { dropped: true, success: true, nodeId: newNode.id }
        }
        return { dropped: false }
      }

      // Create appropriate node based on content card item type
      const cardItem = item as ContentCardDropItem
      let newNode: GenieCanvasNode | null = null
      const parentNodeId = cardItem.metadata?.generatorNodeId

      if (cardItem.type === 'text') {
        newNode = createTextFileNode(
          position,
          typeof cardItem.content === 'string' ? cardItem.content : undefined,
          parentNodeId,
        )
      } else if (cardItem.type === 'image') {
        newNode = createImageFileNode(
          position,
          cardItem.content,
          parentNodeId,
          cardItem.metadata?.dimensions,
        )
      } else if (cardItem.type === 'video') {
        newNode = createVideoFileNode(
          position,
          typeof cardItem.content === 'string' ? cardItem.content : undefined,
          parentNodeId,
        )
      }

      if (newNode && canvasId) {
        // Inject canvasId into node state
        if (newNode.component?.state) {
          newNode.component.state.canvasId = canvasId
        }

        // Copy generatedParams
        if (cardItem.metadata?.generatedParams) {
          const generatedParams = cardItem.metadata.generatedParams
          if (newNode.component?.state) {
            newNode.component.state.modelId = generatedParams.model
            newNode.component.state.settings = generatedParams.parameters
          }
        }

        // Copy generationSnapshot
        if (cardItem.metadata?.generationSnapshot) {
          if (newNode.component?.state) {
            newNode.component.state.generationSnapshot = cardItem.metadata.generationSnapshot as typeof newNode.component.state.generationSnapshot
          }
        }

        const rfNode = toReactFlowNode(newNode, position)

        setNodes?.((prev) => prev.some(n => n.id === rfNode.id) ? prev : [...prev, rfNode])

        // Auto-connect to parent if parentNodeId exists
        if (parentNodeId) {
          const parentNode = nodes.find((n) => n.id === parentNodeId)
          if (!parentNode) return { dropped: false }
          const parentHandles = getNodeHandles(parentNode)
          const childHandles = getNodeHandles(rfNode)
          const sourceHandleId =
            parentHandles.find((h) => h.id === 'generated-output')?.id
            || parentHandles.find((h) => h.type === 'output')?.id
          const targetHandleId =
            childHandles.find((h) => h.id === 'parent-input')?.id
            || childHandles.find((h) => h.type === 'input')?.id

          const newEdge: Edge = {
            id: `edge-${parentNodeId}-${newNode.id}-${Date.now()}`,
            type: 'bezier',
            source: parentNodeId,
            target: newNode.id,
            ...(sourceHandleId ? { sourceHandle: sourceHandleId } : {}),
            ...(targetHandleId ? { targetHandle: targetHandleId } : {}),
          }

          // Wait for node mount + handle measurement before adding edge.
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              const updateFn = useCanvasBridgeStore.getState().updateNodeInternals
              if (typeof updateFn === 'function') {
                updateFn(parentNodeId)
                updateFn(newNode.id)
              }
              setEdges?.((prev) => [...prev, newEdge])
            })
          })
        }

        return { dropped: true, success: true, nodeId: newNode.id }
      }

      return { dropped: false }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  })

  // Handle drag over — always preventDefault so browser allows drops (react-dnd and native files)
  const handleDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault()
      fileDropHandlers.onDragOver(event as unknown as React.DragEvent<HTMLDivElement>)
    },
    [fileDropHandlers],
  )

  return (
    <div
      ref={drop as unknown as React.LegacyRef<HTMLDivElement>}
      className="h-full w-full"
      style={{
        cursor: isPanning ? 'grabbing' : isSpacePressed ? 'grab' : placementCursor || 'default',
      }}
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      onDragOver={handleDragOver}
      onDrop={fileDropHandlers.onDrop}
    >
      <GenieCanvas
        panMode={isSpacePressed}
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange as OnNodesChange}
        onEdgesChange={onEdgesChange as OnEdgesChange}
        onConnect={onConnect as OnConnect}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodeClick={onNodeClick}
        onEdgeClick={onEdgeClick}
        onPaneClick={onPaneClick}
        onPaneContextMenu={onPaneContextMenu}
        onNodeContextMenu={onNodeContextMenu}
        onEdgeContextMenu={onEdgeContextMenu}
        onNodeDragStop={onNodeDragStop}
        onSelectionChange={onSelectionChange as ((params: { nodes: Node[]; edges: Edge[] }) => void) | undefined}
        onInit={handleInit}
        onMoveEnd={handleMoveEnd}
        showControls={showControls}
        showMiniMap={showMiniMap}
        defaultViewport={viewport}
      />
    </div>
  )
})
